import { useState } from "react";
import { AlertTriangle, ExternalLink, Copy, Check } from "lucide-react";

type Props = {
    title?: string;
    message?: string;
    testId?: string;
};

/**
 * Warme Fallback-Karte im HypeFly-Style. Wird gezeigt wenn WebGL nicht
 * verfügbar ist oder ein Viewer beim Init crasht.
 */
export default function ViewerFallback({
    title = "360°-Ansicht nicht verfügbar",
    message = "Die 360°-Ansicht kann in dieser Umgebung nicht dargestellt werden. Bitte prüfe, ob Hardware-Beschleunigung im Browser aktiviert ist, oder öffne die Seite in einem anderen Browser (Chrome, Firefox, Safari, Edge).",
    testId = "viewer-fallback",
}: Props) {
    const [copied, setCopied] = useState(false);
    const currentUrl =
        typeof window !== "undefined" ? window.location.href : "";

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(currentUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            /* ignore */
        }
    };

    return (
        <div
            data-testid={testId}
            className="w-full aspect-[16/9] sm:aspect-[21/9] flex items-center justify-center px-6 py-10"
            style={{
                background: "hsl(var(--hf-surface))",
                border: "1px solid hsl(var(--hf-line))",
                borderRadius: "6px",
            }}
        >
            <div className="max-w-lg text-center">
                <div
                    className="mx-auto mb-5 inline-flex items-center justify-center w-12 h-12 rounded-full"
                    style={{ background: "hsl(var(--hf-gold-soft) / 0.6)" }}
                >
                    <AlertTriangle
                        size={22}
                        style={{ color: "hsl(var(--hf-gold-dark))" }}
                    />
                </div>
                <div className="hf-eyebrow mb-3">Hinweis · 360°-Viewer</div>
                <h3 className="font-display text-2xl md:text-3xl text-hf-ink mb-3">
                    {title}
                </h3>
                <p className="text-sm md:text-base text-hf-muted leading-relaxed">
                    {message}
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                    <button
                        type="button"
                        onClick={copyUrl}
                        className="hf-btn hf-btn-ghost"
                        data-testid={`${testId}-copy-url`}
                    >
                        {copied ? (
                            <>
                                <Check size={14} /> Link kopiert
                            </>
                        ) : (
                            <>
                                <Copy size={14} /> Link kopieren
                            </>
                        )}
                    </button>
                    <a
                        href={currentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hf-btn hf-btn-primary"
                        data-testid={`${testId}-open-new-tab`}
                    >
                        In neuem Tab öffnen <ExternalLink size={14} />
                    </a>
                </div>
            </div>
        </div>
    );
}
